import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { formatDate, formatIntegerOrDash, formatRupees } from '@utils/format';
import { ResolutionBadge } from './components/cells/ResolutionBadge';
import { SourcePhotoThumb } from './components/cells/SourcePhotoThumb';
import { StatusBadge } from './components/cells/StatusBadge';
import { WhatsAppOrigin } from './components/cells/WhatsAppOrigin';
import {
  ReviewRowActionsCell,
  type ReviewRowActions,
} from './components/cells/ReviewRowActions';
import type { StagingRow } from './schema';

const formatRupeesCell = (
  p: ValueFormatterParams<StagingRow, number | null>,
): string => formatRupees(p.value);

const formatNumberOrDash = (
  p: ValueFormatterParams<StagingRow, number | null>,
): string => formatIntegerOrDash(p.value);

const formatDateCell = (p: ValueFormatterParams<StagingRow, string>): string =>
  formatDate(p.value);

// Resolved rows show the master name; unmapped/ambiguous rows have no series
// yet, so fall back to the raw text the pipeline read (italic, muted).
function nameOrRaw(name: string | null, raw: string | null) {
  if (name) return <span className="text-foreground">{name}</span>;
  return (
    <span className="italic text-foreground-muted" title="Not yet mapped">
      {raw ?? '—'}
    </span>
  );
}

interface BuildArgs extends ReviewRowActions {
  onView: (row: StagingRow) => void;
}

// Column order mirrors Market prices for the shared price fields
// (Date, Crop, Variety, Mandi, Min, Modal, Max, Arrivals, Unit); the
// review-only columns (Photo, Origin, Resolution, Confidence, Flags, Status,
// actions) are the additions that make this a review screen.
export function buildPriceReviewColumns(args: BuildArgs): ColDef<StagingRow>[] {
  const { onView, onApprove, onReject } = args;
  return [
    {
      headerName: 'Photo',
      width: 72,
      pinned: 'left',
      lockPinned: true,
      sortable: false,
      cellRenderer: (p: ICellRendererParams<StagingRow>) =>
        p.data ? <SourcePhotoThumb row={p.data} onView={onView} /> : null,
    },
    {
      colId: 'priceDate',
      field: 'priceDate',
      headerName: 'Date',
      width: 120,
      pinned: 'left',
      lockPinned: true,
      sortable: true,
      valueFormatter: formatDateCell,
    },
    {
      field: 'cropName',
      headerName: 'Crop',
      width: 150,
      cellRenderer: (p: ICellRendererParams<StagingRow>) =>
        p.data ? nameOrRaw(p.data.cropName, p.data.cropText) : null,
    },
    {
      field: 'varietyName',
      headerName: 'Variety',
      width: 150,
      cellRenderer: (p: ICellRendererParams<StagingRow>) =>
        p.data ? nameOrRaw(p.data.varietyName, p.data.varietyText) : null,
    },
    {
      field: 'apmcName',
      headerName: 'Mandi',
      flex: 1,
      minWidth: 170,
      cellRenderer: (p: ICellRendererParams<StagingRow>) =>
        p.data ? nameOrRaw(p.data.apmcName, p.data.apmcText) : null,
    },
    {
      field: 'minPrice',
      headerName: 'Min',
      width: 100,
      type: 'rightAligned',
      valueFormatter: formatRupeesCell,
    },
    {
      field: 'modalPrice',
      headerName: 'Modal',
      width: 110,
      type: 'rightAligned',
      valueFormatter: formatRupeesCell,
    },
    {
      field: 'maxPrice',
      headerName: 'Max',
      width: 100,
      type: 'rightAligned',
      valueFormatter: formatRupeesCell,
    },
    {
      field: 'commodityArrivals',
      headerName: 'Arrivals',
      width: 100,
      type: 'rightAligned',
      valueFormatter: formatNumberOrDash,
    },
    {
      field: 'unit',
      headerName: 'Unit',
      width: 72,
    },
    {
      headerName: 'Origin',
      width: 120,
      sortable: false,
      cellRenderer: () => <WhatsAppOrigin />,
    },
    {
      field: 'resolutionStatus',
      headerName: 'Resolution',
      width: 130,
      sortable: false,
      cellRenderer: (p: ICellRendererParams<StagingRow>) =>
        p.data ? <ResolutionBadge status={p.data.resolutionStatus} /> : null,
    },
    {
      colId: 'confidence',
      field: 'extractionConfidence',
      headerName: 'Conf.',
      width: 96,
      type: 'rightAligned',
      sortable: true,
      valueFormatter: (p: ValueFormatterParams<StagingRow, number | null>) =>
        p.value == null ? '—' : `${Math.round(p.value * 100)}%`,
    },
    {
      field: 'validationFailures',
      headerName: 'Flags',
      width: 120,
      sortable: false,
      cellRenderer: (p: ICellRendererParams<StagingRow>) => {
        const flags = p.data?.validationFailures ?? [];
        if (flags.length === 0) {
          return <span className="text-foreground-muted">—</span>;
        }
        return (
          <span
            className="inline-flex h-[20px] items-center rounded-full bg-error/[0.12] px-2 text-[11px] font-semibold text-error"
            title={flags.join('\n')}
          >
            {flags.length} flag{flags.length > 1 ? 's' : ''}
          </span>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      sortable: false,
      cellRenderer: (p: ICellRendererParams<StagingRow>) =>
        p.data ? <StatusBadge status={p.data.status} /> : null,
    },
    {
      headerName: '',
      width: 200,
      pinned: 'right',
      sortable: false,
      filter: false,
      resizable: false,
      // Actions only on rows still awaiting review; approved/published rows
      // (Published view) are read-only here.
      cellRenderer: (p: ICellRendererParams<StagingRow>) =>
        p.data && p.data.status === 'PENDING' ? (
          <ReviewRowActionsCell
            row={p.data}
            onApprove={onApprove}
            onReject={onReject}
          />
        ) : null,
    },
  ];
}
